import {
  HighlightProps,
  PolygonHighlightProps,
} from 'components/Images/Highlights/HighlightTypes'
import { getHighlightCenterCoordinates } from 'features/Drawings/PdfDrawings/getHighlightCenterCoordinates'
import { BomItemPointer } from 'model/Project/BomItemPointer'
import { useRef, useState } from 'react'
import { SvgShape } from 'services/APIs/InternalAPI/internal-api.contracts'
import { Vect2D } from '../ZoomAndPanImg'
import {
  handleSvgHighlightClicked,
  handleSvgHighlightDoubleClicked,
} from './handleSvgHighlightClicked'
import { HighlightTooltip } from './HighlightTooltip'
import { useHighlightColor } from './useHighlightColor'

const offsetPolygon = (
  points: Vect2D[],
  padding: { pt: number; pr: number; pb: number; pl: number },
) => {
  const shape = {
    shape: 'polygon',
    points: points.map((point) => {
      return `${point.x},${point.y}`
    }),
  } as SvgShape

  const shapeCenter = getHighlightCenterCoordinates(shape)

  const offsetPoints = points.map((point) => {
    let x = point.x
    let y = point.y

    if (x > shapeCenter.x) {
      x = point.x + padding.pr
    } else {
      x = point.x - padding.pl
    }

    if (y > shapeCenter.y) {
      y = point.y + padding.pb
    } else {
      y = point.y - padding.pt
    }

    return { x, y }
  })

  return offsetPoints.map((x) => `${x.x},${x.y}`).join(' ')
}

export const PolygonHighlight = (
  props: HighlightProps<PolygonHighlightProps> & {
    bomItemPointer: BomItemPointer
  },
) => {
  const { fillColor, opacity, classNames, activeStrokeColor } =
    useHighlightColor({
      isActiveCheckListFeature: props.isActiveCheckListFeature,
      isDoneCheckListFeature: props.isDoneCheckListFeature,
      isCheckListFeature: props.isCheckListFeature,
    })

  const [open, setOpen] = useState(false)

  const polygonPoints = offsetPolygon(props.points, {
    pt: 0,
    pr: 4,
    pb: 0,
    pl: 4,
  })

  const clickStart = useRef<{ x: number; y: number } | null>(null)

  const ignore = false

  if (ignore) {
    return null
  }

  return (
    <HighlightTooltip
      {...props}
      open={open}
      setOpen={setOpen}
      enableHoverListener
    >
      <g>
        <polygon
          data-filterid={props.pdfFeature?.id}
          data-filtercontent={props.pdfFeature?.clipboardContent}
          points={polygonPoints}
          className={classNames}
          fill={fillColor.main}
          style={{
            cursor: 'pointer',
            opacity: 0.5,
          }}
          stroke={
            props.isActiveCheckListFeature ? activeStrokeColor : fillColor.dark
          }
          strokeWidth={props.isActiveCheckListFeature ? 6 : 6}
          strokeOpacity={props.isActiveCheckListFeature ? 1 : opacity}
          onMouseDown={(e) => {
            if (e.button !== 0) {
              return
            }

            e.stopPropagation()
            e.preventDefault()

            clickStart.current = { x: e.clientX, y: e.clientY }
          }}
          onMouseUp={(e) => {
            if (e.button !== 0) {
              return
            }

            e.stopPropagation()
            e.preventDefault()

            const clickEnd = { x: e.clientX, y: e.clientY }
            const ratioX = clickEnd.x / clickStart.current?.x || clickEnd.x
            const ratioY = clickEnd.y / clickStart.current?.y || clickEnd.y

            if (Math.abs(ratioX - 1) < 0.01 && Math.abs(ratioY - 1) < 0.01) {
              handleSvgHighlightClicked(
                props.id,
                props.pdfPage,
                props.pdfFeature,
              )
            } else {
              console.info('mouse moved, not triggering click')
            }

            clickStart.current = null
          }}
          onDoubleClick={(e) => {
            e.preventDefault()
            e.stopPropagation()

            handleSvgHighlightDoubleClicked(props.pdfFeature)
          }}
          // strokeDasharray={props.isActiveCheckListFeature ? '1000' : '0'}
          // strokeDasharray={5}
          strokeDasharray={props.isActiveCheckListFeature ? '16 10' : '0'}
          strokeDashoffset={0}
          filter={props.isActiveCheckListFeature ? 'url(#glow)' : undefined}
        >
          {props.isActiveCheckListFeature && (
            <>
              <animate
                attributeName="stroke-dashoffset"
                from="0"
                to="-80"
                dur="2s"
                repeatCount={'indefinite'}
              />
            </>
          )}
        </polygon>
      </g>
    </HighlightTooltip>
  )
}
